import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import { useColorScheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { isEmpty, isNil } from 'ramda';

import { EMPTY_VALUES } from 'constants/general';

export const CardRange = ({
	unit = '%',
	maxValue = 100,
	text,
	subText,
	onChange,
	value = 0,
	type = 'info',
	disabled,
}) => {
	const subTextRef = useRef(null);
	const textFieldRef = useRef(null);
	const { mode: appTheme } = useColorScheme();

	const [showTooltipHover, setShowTooltipHover] = useState(false);

	const [sliderValue, setSliderValue] = useState(value);
	const [tempTextFieldValue, setTempTextFieldValue] = useState(null);
	const [active, setActive] = useState(false);
	const [textFieldTooltipOpen, setTextFieldTooltipOpen] = useState(false);

	const compareSize = useCallback(() => {
		const compare = subTextRef.current.scrollWidth > subTextRef.current.clientWidth;
		setShowTooltipHover(compare);
	}, []);

	// compare once and add resize listener on "componentDidMount"
	useEffect(() => {
		compareSize();
		window.addEventListener('resize', compareSize);

		return () => window.removeEventListener('resize', compareSize);
	}, [compareSize]);

	useEffect(() => {
		setSliderValue(value);
	}, [value]);

	const handleTooltip = useCallback(() => {
		const compare = textFieldRef.current.scrollWidth > textFieldRef.current.clientWidth;
		setTextFieldTooltipOpen(compare);
	}, []);

	const handleCloseTooltip = useCallback(() => {
		setTextFieldTooltipOpen(false);
	}, []);

	const handleMoveSlider = useCallback(
		(e, value) => {
			onChange && onChange(value);
			setActive(false);
		},
		[onChange],
	);

	const handleDrag = useCallback((e, value) => {
		setSliderValue(value);
		setActive(true);
	}, []);

	const handleInputChange = useCallback(
		(event) => {
			const numberVal = Number(event.target.value);
			if (isNaN(numberVal)) {
				return;
			} else if (numberVal > maxValue) {
				setSliderValue(maxValue);
				onChange(maxValue);
			} else {
				setSliderValue(numberVal);
				onChange(String(numberVal));
			}

			setTempTextFieldValue(null);
		},
		[maxValue, onChange],
	);

	const handleFocus = useCallback(
		(e) => {
			setSliderValue(EMPTY_VALUES.STRING); // temporarily resetting field value
			setTempTextFieldValue(Number(e.target.value));
			setActive(true);
			handleCloseTooltip();
		},
		[handleCloseTooltip],
	);

	const handleBlur = useCallback(() => {
		setActive(false);

		if (!isNil(tempTextFieldValue)) {
			if (!isEmpty(sliderValue) && sliderValue !== tempTextFieldValue) {
				setSliderValue(sliderValue);
			} else {
				setSliderValue(tempTextFieldValue);
			}
		}

		setTempTextFieldValue(null);
	}, [tempTextFieldValue, sliderValue]);

	const valueBackgroundColor = useMemo(() => {
		if (disabled) return 'grey.400';
		if (active) {
			return 'common.white';
		} else {
			return type === 'error' ? 'primary.main' : 'info.shades.50p';
		}
	}, [active, disabled, type]);

	const unitColor = useMemo(() => {
		if (disabled || active) {
			return 'common.black';
		} else {
			return 'common.white';
		}
	}, [disabled, active]);

	return (
		<Stack
			direction="row"
			sx={{
				backgroundColor: appTheme === 'light' ? 'common.white' : 'background.300',
				boxShadow: '0 0 13px 0 rgba(0,0,0,.24)',
				borderRadius: '5px',
				height: '65px',
				border: (theme) =>
					active
						? `2px solid ${type === 'error' ? theme.palette.primary.main : theme.palette.info.main}`
						: 'none',
			}}
		>
			<Stack sx={{ width: '70%', px: 2, py: 1, position: 'relative' }}>
				<Typography variant="h6" color="text.primary">
					{text}
				</Typography>
				<Tooltip title={subText} interactive disableFocusListener disableHoverListener={!showTooltipHover}>
					<Typography noWrap ref={subTextRef} variant="caption">
						{subText}
					</Typography>
				</Tooltip>
				<Slider
					sx={{
						bottom: -15,
						width: '85%',
						position: 'absolute',
						'& .MuiSlider-rail': {
							opacity: 0,
						},
						'& .MuiSlider-track': {
							opacity: 0,
						},
					}}
					value={sliderValue}
					onChange={handleDrag}
					onChangeCommitted={handleMoveSlider}
					disabled={disabled}
					max={maxValue}
				/>
			</Stack>
			<Stack
				sx={{
					width: '33%',
					backgroundColor: valueBackgroundColor,
					borderRadius: '0 5px 5px 0',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<Stack
					direction="row"
					sx={{
						alignItems: 'center',
					}}
				>
					<Tooltip title={sliderValue} interactive open={textFieldTooltipOpen}>
						<div onMouseEnter={handleTooltip} onMouseLeave={handleCloseTooltip}>
							<TextField
								inputRef={textFieldRef}
								sx={{
									p: 0.5,
									width: '50px',
									'& .MuiInputBase-input': {
										overflow: 'hidden',
										textOverflow: 'ellipsis',
									},
								}}
								size="small"
								variant="standard"
								value={sliderValue}
								onChange={handleInputChange}
								onFocus={handleFocus}
								onBlur={handleBlur}
								disabled={disabled}
								autoComplete="off"
								slotProps={{
									input: {
										disableUnderline: true,
										inputProps: {
											style: {
												textAlign: 'right',
												color: active ? 'black' : 'white',
												fontWeight: 500,
												paddingBottom: '2px',
											},
										},
									},
								}}
							/>
						</div>
					</Tooltip>
					<Typography
						sx={{
							color: unitColor,
						}}
					>
						{unit}
					</Typography>
				</Stack>
			</Stack>
		</Stack>
	);
};

CardRange.propTypes = {
	disabled: PropTypes.bool,
	maxValue: PropTypes.number,
	onChange: PropTypes.func,
	subText: PropTypes.string,
	text: PropTypes.string,
	type: PropTypes.string,
	unit: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default CardRange;
